import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import { PaginationProps } from "../../shared/types";
import { LIST_PAGE_SIZE_OPTIONS } from "../../shared/constants/pagination";
import { getPaginationTheme } from "../../shared/theme/wiserTheme";

const PaginationDark: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalCount,
  itemsPerPage,
  onItemsPerPageChange,
  showItemsPerPageSelect = true,
}) => {
  const [goToPage, setGoToPage] = useState<string>("");
  const pt = getPaginationTheme(true);

  const handleGoToPage = () => {
    const pageNumber = parseInt(goToPage);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
      setGoToPage("");
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleGoToPage();
    }
  };

  const getPages = (current: number, total: number): (number | "...")[] => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 3) return [1, 2, 3, 4, 5, "...", total];
    if (current >= total - 2)
      return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
    return [1, "...", current - 1, current, current + 1, "...", total];
  };

  const visiblePages = getPages(currentPage, totalPages);

  if (!totalCount || totalCount === 0) {
    return null;
  }

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount || 0);

  const labelSx = {
    fontFamily: "Inter",
    fontStyle: "normal" as const,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "20px",
    color: pt.textPrimary,
  };

  const outlinedInputSx = {
    "& .MuiOutlinedInput-root": {
      height: "28px",
      borderRadius: "3px",
      background: pt.surface,
      "& fieldset": { borderColor: pt.border },
      "&:hover fieldset": { borderColor: pt.textSecondary },
      "&.Mui-focused fieldset": { borderColor: pt.focusBorder },
    },
  };

  return (
    <Box
      sx={{
        mt: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 0.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          width: "100%",
          py: 1,
          flexWrap: "wrap",
          gap: "30px",
        }}
      >
        {totalPages > 1 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={labelSx}>Sayfa</Typography>
            <TextField
              size="small"
              type="number"
              value={goToPage}
              onChange={(e) => setGoToPage(e.target.value)}
              onKeyPress={handleKeyPress}
              inputProps={{
                min: 1,
                max: totalPages,
                style: {
                  padding: "4px 8px",
                  fontSize: "14px",
                  textAlign: "center",
                  width: "50px",
                  height: "20px",
                  color: pt.textPrimary,
                },
              }}
              sx={outlinedInputSx}
            />
            <Typography sx={{ fontSize: 14, color: pt.textSecondary }}>
              / {totalPages}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleGoToPage}
              disabled={
                !goToPage ||
                parseInt(goToPage) < 1 ||
                parseInt(goToPage) > totalPages
              }
              sx={{
                minWidth: "50px",
                height: "28px",
                textTransform: "none",
                borderRadius: "3px",
                borderColor: pt.border,
                "&:hover": {
                  borderColor: pt.textSecondary,
                  backgroundColor: pt.hoverBg,
                },
                ...labelSx,
              }}
            >
              Git
            </Button>
          </Box>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: "flex", alignItems: "center", height: 32, gap: 0.5 }}>
            <Box
              role="button"
              tabIndex={0}
              onClick={() =>
                currentPage > 1 && onPageChange(Math.max(1, currentPage - 1))
              }
              sx={{
                width: 33,
                height: 36,
                border: `1px solid ${pt.border}`,
                borderRadius: "3px",
                display: "grid",
                placeItems: "center",
                cursor: currentPage > 1 ? "pointer" : "not-allowed",
                opacity: currentPage > 1 ? 1 : 0.5,
                backgroundColor: pt.surface,
                "&:hover": {
                  backgroundColor: currentPage > 1 ? pt.hoverBg : pt.surface,
                },
              }}
            >
              <ArrowDropDown
                style={{ transform: "rotate(90deg)", color: pt.textSecondary }}
              />
            </Box>

            {visiblePages.map((page, idx) =>
              page === "..." ? (
                <Box
                  key={`dots-${idx}`}
                  sx={{
                    width: 33,
                    height: 36,
                    cursor: "pointer",
                    background: pt.surface,
                    border: `1px solid ${pt.border}`,
                    borderRadius: "3px",
                    display: "grid",
                    placeItems: "center",
                    px: 1,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      color: pt.textSecondary,
                      fontSize: 14,
                      fontFamily: "inherit",
                    }}
                  >
                    …
                  </Box>
                </Box>
              ) : (
                <Box
                  key={`p-${page}`}
                  onClick={() =>
                    page !== currentPage ? onPageChange(Number(page)) : null
                  }
                  sx={{
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "8px 12px",
                    width: 33,
                    height: 36,
                    background:
                      page === currentPage ? pt.activeBg : pt.surface,
                    border: `1px solid ${pt.border}`,
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      color:
                        page === currentPage ? pt.activeText : pt.textPrimary,
                      fontSize: 14,
                      fontFamily: "inherit",
                      fontWeight: page === currentPage ? 600 : 400,
                    }}
                  >
                    {page}
                  </Box>
                </Box>
              ),
            )}

            <Box
              role="button"
              tabIndex={0}
              onClick={() =>
                currentPage < totalPages &&
                onPageChange(Math.min(totalPages, currentPage + 1))
              }
              sx={{
                width: 33,
                height: 36,
                border: `1px solid ${pt.border}`,
                borderRadius: "3px",
                display: "grid",
                placeItems: "center",
                cursor: currentPage < totalPages ? "pointer" : "not-allowed",
                opacity: currentPage < totalPages ? 1 : 0.5,
                backgroundColor: pt.surface,
                "&:hover": {
                  backgroundColor:
                    currentPage < totalPages ? pt.hoverBg : pt.surface,
                },
              }}
            >
              <ArrowDropDown
                style={{ transform: "rotate(-90deg)", color: pt.textSecondary }}
              />
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {showItemsPerPageSelect && onItemsPerPageChange && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={labelSx}>
                Sayfa başına gösterilen sonuç
              </Typography>
              <FormControl size="small">
                <Select
                  value={itemsPerPage}
                  onChange={(e) =>
                    onItemsPerPageChange(Number(e.target.value))
                  }
                  sx={{
                    height: "28px",
                    fontSize: "14px",
                    minWidth: "60px",
                    color: pt.textPrimary,
                    backgroundColor: pt.surface,
                    "& fieldset": { borderColor: pt.border },
                    "&:hover fieldset": { borderColor: pt.textSecondary },
                    "&.Mui-focused fieldset": { borderColor: pt.focusBorder },
                  }}
                >
                  {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Typography variant="body2" sx={labelSx}>
            {startItem}-{endItem} / {totalCount}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PaginationDark;
